/**
 * Catalog warranty type + months. Empty type = no warranty (placeholder, not prefilled).
 */
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectPlaceholder,
  TextField,
} from '@/shared/ui';
import type { WarrantyType } from '../types/treatmentCatalog.types';

interface WarrantyCatalogFieldsProps {
  warrantyType: WarrantyType | '';
  warrantyMonths: string;
  warrantyMonthsError?: string;
  disabled?: boolean;
  onChange: (patch: { warrantyType?: WarrantyType | ''; warrantyMonths?: string }) => void;
}

const WarrantyCatalogFields = ({
  warrantyType,
  warrantyMonths,
  warrantyMonthsError,
  disabled = false,
  onChange,
}: WarrantyCatalogFieldsProps) => {
  const { t } = useTranslation('treatment');

  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <FormControl fullWidth disabled={disabled}>
          <InputLabel shrink>{t('fields.warrantyType')}</InputLabel>
          <Select
            name="warrantyType"
            value={warrantyType}
            displayEmpty
            notched
            disabled={disabled}
            renderValue={(selected) => {
              const value = String(selected ?? '');
              if (!value) {
                return (
                  <SelectPlaceholder>{t('catalog.placeholders.warrantyType')}</SelectPlaceholder>
                );
              }
              if (value === 'months') return t('catalog.warranty.fixedTerm');
              if (value === 'lifetime') return t('catalog.warranty.lifetime');
              return value;
            }}
            onChange={(e) => {
              const next = e.target.value as WarrantyType | '';
              onChange({
                warrantyType: next,
                ...(next === 'months' ? {} : { warrantyMonths: '' }),
              });
            }}
            sx={{
              '&:not(:hover):not(:focus-within) .MuiSelect-icon': { display: 'none' },
              '&:not(:hover):not(:focus-within) .MuiSelect-select': { pr: '8px !important' },
            }}
          >
            <MenuItem value="">{t('catalog.warranty.none')}</MenuItem>
            <MenuItem value="months">{t('catalog.warranty.fixedTerm')}</MenuItem>
            <MenuItem value="lifetime">{t('catalog.warranty.lifetime')}</MenuItem>
          </Select>
        </FormControl>
        {warrantyType === 'months' ? (
          <TextField
            fullWidth
            label={t('fields.warrantyMonths')}
            name="warrantyMonths"
            type="number"
            value={warrantyMonths}
            onChange={(e) => onChange({ warrantyMonths: e.target.value })}
            error={Boolean(warrantyMonthsError)}
            helperText={warrantyMonthsError}
            required
            disabled={disabled}
            slotProps={{
              htmlInput: { min: 1, step: 1 },
            }}
          />
        ) : null}
      </Box>
    </Grid>
  );
};

export default WarrantyCatalogFields;
